import { useCallback, useState } from 'react';

export type GeoStatus = 'idle' | 'locating' | 'granted' | 'denied' | 'unavailable';

export interface GeolocationState {
  status: GeoStatus;
  coords: { lat: number; lng: number } | null;
  request: () => void;
}

/**
 * Thin wrapper around navigator.geolocation.getCurrentPosition for the
 * marketplace "Search near me" feature. No UI. Any failure (permission denied,
 * timeout, position-unavailable) collapses to 'denied' so the caller can fall
 * back to manual city search; a missing API → 'unavailable'.
 */
export function useGeolocation(): GeolocationState {
  const [status, setStatus] = useState<GeoStatus>('idle');
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);

  const request = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setStatus('unavailable');
      return;
    }
    setStatus('locating');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setStatus('granted');
      },
      () => {
        setCoords(null);
        setStatus('denied');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  }, []);

  return { status, coords, request };
}
